import nipplejs from 'nipplejs';

import * as entity from "../base/entity.js";
import * as passes from '../base/passes.js';
import {IS_MOBILE} from '../base/quality.js';


// Input layer ported from surreal-meadow: touch-primary devices get two
// on-screen nipplejs joysticks (left = move, right = look) plus a jump
// button; desktops get WASD/arrows + pointer-lock mouse; a gamepad works
// on either. Every source feeds the same move/look vectors, so the
// movement physics in player-entity.js never cares where input came from.
export const player_input = (() => {

  const IS_TOUCH = IS_MOBILE;

  // Visual joystick size is 100px, but full output (f=1) is reached at
  // 45px of drag so the sticks feel responsive without big thumb sweeps.
  const JOYSTICK_MAX_DRAG_PX = 45;

  const GAMEPAD_DEADZONE = 0.15;
  function applyDeadzone(v) {
    return Math.abs(v) < GAMEPAD_DEADZONE ? 0 : v;
  }

  class PlayerInput extends entity.Component {
    static CLASS_NAME = 'PlayerInput';

    get NAME() {
      return PlayerInput.CLASS_NAME;
    }

    constructor(params) {
      super();
      this.params_ = params;
    }

    InitEntity() {
      // Combined per-frame input, composed in Update() from every source.
      // move: x = strafe right(+)/left(-), y = forward(+)/back(-).
      // look: velocity-based (joystick/gamepad), applied per second.
      // mouseDelta: direct pixel deltas (pointer-lock mouse only).
      this.current_ = {
        moveX: 0, moveY: 0,
        lookX: 0, lookY: 0,
        mouseDeltaX: 0, mouseDeltaY: 0,
      };

      // Raw joystick state, written by nipplejs callbacks.
      this.moveVector_ = { x: 0, y: 0 };
      this.lookVector_ = { x: 0, y: 0 };

      this.keys_ = Object.create(null);
      this.mouseAccumX_ = 0;
      this.mouseAccumY_ = 0;
      this.jumpRequested_ = false;
      this.gamepadJumpWasPressed_ = false;

      this.SetupKeyboard_();
      this.SetupMouse_();

      if (IS_TOUCH) {
        this.SetupTouch_();
      } else {
        // Hides the joystick zones / jump button via CSS.
        document.body.classList.add('desktop');
      }

      this.SetPass(passes.Passes.INPUT);
    }

    // -----------------------------------------------------------------
    // Keyboard: WASD/arrows to move, Space to jump.
    // -----------------------------------------------------------------
    SetupKeyboard_() {
      window.addEventListener('keydown', (e) => {
        if (e.target && e.target.tagName === 'INPUT') return;
        this.keys_[e.code] = true;
        if (e.code === 'Space') {
          if (!e.repeat) this.jumpRequested_ = true;
          e.preventDefault(); // don't scroll the page on Space
        }
        if (e.code.startsWith('Arrow')) e.preventDefault();
      }, false);
      window.addEventListener('keyup', (e) => {
        if (e.target && e.target.tagName === 'INPUT') return;
        this.keys_[e.code] = false;
      }, false);
    }

    GetKeyboardMove_() {
      let x = 0, y = 0;
      if (this.keys_['KeyW'] || this.keys_['ArrowUp'])    y += 1;
      if (this.keys_['KeyS'] || this.keys_['ArrowDown'])  y -= 1;
      if (this.keys_['KeyD'] || this.keys_['ArrowRight']) x += 1;
      if (this.keys_['KeyA'] || this.keys_['ArrowLeft'])  x -= 1;
      return { x, y };
    }

    // -----------------------------------------------------------------
    // Mouse: pointer-lock look. movementX/Y are raw pixel deltas, so look
    // is direct (not velocity-based like the joystick/gamepad).
    // -----------------------------------------------------------------
    SetupMouse_() {
      if (IS_TOUCH) return;

      // Engage pointer lock with unadjustedMovement so the OS's mouse
      // acceleration/ballistics don't amplify fast motion into large,
      // erratic deltas. Falls back to a plain lock where unsupported.
      document.addEventListener('click', () => {
        if (document.pointerLockElement) return;
        const el = document.body;
        const res = el.requestPointerLock({ unadjustedMovement: true });
        if (res && typeof res.catch === 'function') {
          res.catch(() => { try { el.requestPointerLock(); } catch {} });
        }
      }, false);

      // Chrome reports one huge movementX/Y on the first mousemove after
      // the lock engages (the delta from the old cursor position). Skip
      // exactly that event.
      this.skipNextMouseMove_ = false;
      document.addEventListener('pointerlockchange', () => {
        if (document.pointerLockElement) this.skipNextMouseMove_ = true;
      });

      // Last-resort guard against a pathological delta; no real motion
      // (even a hard flick) ever reaches it.
      const MAX_MOUSE_DELTA = 1500;
      document.addEventListener('mousemove', (e) => {
        if (!document.pointerLockElement) return;
        if (this.skipNextMouseMove_) { this.skipNextMouseMove_ = false; return; }
        const dx = e.movementX, dy = e.movementY;
        if (Math.abs(dx) > MAX_MOUSE_DELTA || Math.abs(dy) > MAX_MOUSE_DELTA) return;
        this.mouseAccumX_ += dx;
        this.mouseAccumY_ += dy;
      });
    }

    // -----------------------------------------------------------------
    // Touch: two static joysticks and a jump button.
    // -----------------------------------------------------------------
    SetupTouch_() {
      this.leftStick_ = this.CreateLeftStick_();
      this.rightStick_ = this.CreateRightStick_();

      // nipplejs can lose track of a touch if its touchend/touchcancel
      // never arrives (e.g. the OS swallows it for a system gesture),
      // leaving the stick's knob and vector stuck off-center. Once no
      // touches remain on screen, force a reset of any stick that still
      // thinks it's active.
      const handleGlobalTouchEnd = (e) => {
        if (e.touches.length > 0) return;
        if (this.moveVector_.x !== 0 || this.moveVector_.y !== 0) {
          this.moveVector_.x = 0;
          this.moveVector_.y = 0;
          this.leftStick_.destroy();
          this.leftStick_ = this.CreateLeftStick_();
        }
        if (this.lookVector_.x !== 0 || this.lookVector_.y !== 0) {
          this.lookVector_.x = 0;
          this.lookVector_.y = 0;
          this.rightStick_.destroy();
          this.rightStick_ = this.CreateRightStick_();
        }
      };
      document.addEventListener('touchend', handleGlobalTouchEnd, { passive: true });
      document.addEventListener('touchcancel', handleGlobalTouchEnd, { passive: true });

      const jumpButton = document.getElementById('jump-button');
      jumpButton.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        jumpButton.classList.add('active');
        this.jumpRequested_ = true;
      });
      jumpButton.addEventListener('pointerup', () => jumpButton.classList.remove('active'));
      jumpButton.addEventListener('pointerleave', () => jumpButton.classList.remove('active'));

      // Prevent pinch-zoom and double-tap-to-zoom (some mobile browsers
      // still trigger these even with user-scalable=no, especially iOS
      // Safari). Two-finger touches happen constantly here - one thumb
      // per joystick - so any pinch gesture must be blocked outright.
      let lastTouchEnd = 0;
      document.addEventListener('touchend', (e) => {
        const now = Date.now();
        if (now - lastTouchEnd <= 350) e.preventDefault();
        lastTouchEnd = now;
      }, { passive: false });
      document.addEventListener('touchmove', (e) => {
        if (e.touches.length > 1) e.preventDefault();
      }, { passive: false });
      document.addEventListener('gesturestart', (e) => e.preventDefault());
      document.addEventListener('gesturechange', (e) => e.preventDefault());
      document.addEventListener('gestureend', (e) => e.preventDefault());
    }

    CreateLeftStick_() {
      const stick = nipplejs.create({
        zone: document.getElementById('joystick-left'),
        mode: 'static',
        position: { left: '50%', top: '50%' },
        color: 'white',
        size: 100,
      });
      stick.on('move', (evt, data) => {
        const f = Math.min(data.distance / JOYSTICK_MAX_DRAG_PX, 1);
        this.moveVector_.x = Math.cos(data.angle.radian) * f;
        this.moveVector_.y = Math.sin(data.angle.radian) * f;
      });
      stick.on('end', () => { this.moveVector_.x = 0; this.moveVector_.y = 0; });
      return stick;
    }

    CreateRightStick_() {
      const stick = nipplejs.create({
        zone: document.getElementById('joystick-right'),
        mode: 'static',
        position: { left: '50%', top: '50%' },
        color: 'white',
        size: 100,
      });
      stick.on('move', (evt, data) => {
        const f = Math.min(data.distance / JOYSTICK_MAX_DRAG_PX, 1);
        this.lookVector_.x = Math.cos(data.angle.radian) * f;
        this.lookVector_.y = Math.sin(data.angle.radian) * f;
      });
      stick.on('end', () => {
        this.lookVector_.x = 0;
        this.lookVector_.y = 0;
      });
      return stick;
    }

    // -----------------------------------------------------------------
    // Gamepad: left stick to move, right stick to look, A to jump.
    // -----------------------------------------------------------------
    GetGamepadInput_() {
      const pads = navigator.getGamepads ? navigator.getGamepads() : [];
      for (const gp of pads) {
        if (gp) {
          return {
            moveX: applyDeadzone(gp.axes[0] || 0),
            moveY: -applyDeadzone(gp.axes[1] || 0),
            lookX: applyDeadzone(gp.axes[2] || 0),
            lookY: -applyDeadzone(gp.axes[3] || 0),
            jumpPressed: !!(gp.buttons[0] && gp.buttons[0].pressed),
          };
        }
      }
      return null;
    }

    // One-shot jump flag, consumed by the character controller (which
    // runs its own jump buffering on top).
    ConsumeJump() {
      const j = this.jumpRequested_;
      this.jumpRequested_ = false;
      return j;
    }

    Update(_) {
      const gp = this.GetGamepadInput_();
      const km = this.GetKeyboardMove_();

      if (gp) {
        // Edge-detect the gamepad jump button so holding it doesn't
        // repeat-jump.
        if (gp.jumpPressed && !this.gamepadJumpWasPressed_) {
          this.jumpRequested_ = true;
        }
        this.gamepadJumpWasPressed_ = gp.jumpPressed;
      }

      const c = this.current_;
      c.moveX = Math.max(-1, Math.min(1,
          this.moveVector_.x + (gp ? gp.moveX : 0) + km.x));
      c.moveY = Math.max(-1, Math.min(1,
          this.moveVector_.y + (gp ? gp.moveY : 0) + km.y));
      c.lookX = this.lookVector_.x + (gp ? gp.lookX : 0);
      c.lookY = this.lookVector_.y + (gp ? gp.lookY : 0);
      c.mouseDeltaX = this.mouseAccumX_;
      c.mouseDeltaY = this.mouseAccumY_;
      this.mouseAccumX_ = 0;
      this.mouseAccumY_ = 0;
    }
  };

  return {
    PlayerInput: PlayerInput,
  };

})();
